import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FiX } from "react-icons/fi";
import { theme } from "../../styles/theme";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Label, ErrorMessage } from "../ui/Input";
import { remixProject } from "../../services/api";
import { remixTemplates } from "../../constants/remixTemplates";

const Layout = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: auto;
  min-height: 5em;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  font-family: inherit;
  resize: vertical;
  line-height: 1.4;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const UrlInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
`;

const TemplateRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  overflow-x: auto;
  padding: ${theme.spacing["2xs"]} 0;
`;

const TemplateButton = styled.button`
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.pill};
  padding: ${theme.spacing["2xs"]} ${theme.spacing.sm};
  cursor: pointer;
  white-space: nowrap;
  font-size: ${theme.typography.fontSize.sm};
`;

function RemixDialog({ isOpen, project, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [additionalUrl, setAdditionalUrl] = useState("");
  const [quality, setQuality] = useState("pro");

  useEffect(() => {
    if (isOpen && project) {
      setPrompt("");
      setError("");
      setAdditionalUrl("");
      setQuality("pro");
    }
  }, [isOpen, project]);

  if (!project) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!prompt.trim()) {
      setError("Please enter remix instructions");
      return;
    }

    setLoading(true);
    setError("");
    const urls = additionalUrl.trim() ? [additionalUrl.trim()] : [];
    onClose?.();
    remixProject(project.id, prompt.trim(), urls, quality).catch((err) => {
      // eslint-disable-next-line no-console
      console.error("Failed to start remix", err);
    });
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={loading ? undefined : onClose}>
      <Layout onSubmit={handleSubmit}>
        <Header>
          <h3 style={{ margin: 0 }}>Remix project</h3>
          <Button variant="ghost" type="button" onClick={onClose} disabled={loading}>
            <FiX />
          </Button>
        </Header>

        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label htmlFor="remix-prompt">Remix prompt</Label>
          <TemplateRow>
            {remixTemplates.map((tpl) => (
              <TemplateButton
                key={tpl.name}
                type="button"
                onClick={() =>
                  setPrompt((prev) =>
                    prev ? `${prev}\n\n${tpl.prompt}` : tpl.prompt
                  )
                }
                disabled={loading}
                title={tpl.prompt}
              >
                {tpl.name}
              </TemplateButton>
            ))}
          </TemplateRow>
          <TextArea
            id="remix-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            disabled={loading}
            placeholder="e.g., Add neon lights, make outfit red, etc."
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>


        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label htmlFor="remix-quality">Quality</Label>
          <select
            id="remix-quality"
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
            disabled={loading}
            style={{
              padding: theme.spacing.sm,
              borderRadius: theme.borderRadius.md,
              border: theme.borders.base,
              width: "100%",
            }}
          >
            <option value="pro">PRO (default)</option>
            <option value="fast">FAST (seedream 4.5)</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing["2xs"] }}>
          <Label htmlFor="remix-image-url">Reference image URL (optional)</Label>
          <UrlInput
            id="remix-image-url"
            type="url"
            value={additionalUrl}
            onChange={(e) => setAdditionalUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            disabled={loading}
          />
        </div>

        <Actions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Remix image"}
          </Button>
        </Actions>
      </Layout>
    </Modal>
  );
}

export default RemixDialog;
